import React, {FC, FormEvent, SyntheticEvent, useEffect, useState} from 'react';

import {Checkbox, CtaButton} from '@optimaxdev/design-system';
import classNames from 'classnames';

import {useFeature} from 'components/feature';
import {PAYMENT_OS_SCRIPT_SRC} from 'constants/checkout';
import {Input} from 'desktop/components/input/input';
import {useLoadScriptWithCheck} from 'hooks/useLoadScript/useLoadScript';

import {usePaymentOs} from '../../hooks/usePaymentOs/usePaymentOS';
import {PaymentCvvTooltip} from '../paymentCvvTooltip/paymentCvvTooltip';

import {fields} from './paymentOsCardFormV3.util';

import s from './paymentOsCardForm.scss';

type AdditionalDetailsType = {
    shouldSave: boolean;
};

type PaymentDataType = {
    token: string;
    cvv: string;
};

export type PropsType = {
    onSubmit: (data: PaymentDataType, additional: AdditionalDetailsType) => void;
    cardholderName?: string;
    withSave: boolean;
    isSideReview?: boolean;
    button?: (isLoading: boolean, onSubmit: () => void) => React.ReactNode;
    onSaveCardChange?: (value?: boolean) => void;
    onError?: (value?: string, isInteractive?: boolean) => void;
    onInvalid?: (event?: FormEvent<HTMLInputElement>) => void;
    children?: React.ReactNode;
};

/**
 * PaymentsOS card form
 *
 * @param {PropsType} props - properties
 * @returns {JSX.Element} component
 */
const PaymentOsCardFormV3: FC<PropsType> = ({
    button,
    cardholderName,
    children,
    isSideReview = false,
    onError,
    onInvalid,
    onSaveCardChange,
    onSubmit,
    withSave,
}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const [data, saveData] = useState({
        cardholderName: '',
        cardNumber: null,
        env: '',
        isProgress: false,
        saveCard: false,
    });

    const {createToken, errors} = usePaymentOs(fields);

    useEffect(() => {
        if (onError && errors) onError(errors);
    }, [errors, onError]);

    const handleOnSubmit = async (event?: FormEvent<HTMLFormElement>) => {
        if (event) event.preventDefault();

        saveData({...data, isProgress: true});

        const additionalData = {
            additional_details: {
                tmp: data.saveCard ? '0' : '1',
            },
            holder_name: cardholderName || data.cardholderName,
        };

        createToken(additionalData, {
            onReject: error => {
                if (onError) {
                    onError(error, true);
                }
            },
            onSuccess: tokenData => {
                onSubmit(tokenData, {shouldSave: data.saveCard});
            },
        });

        saveData({...data, isProgress: false});
    };

    const handleChangeSaveCard = () => {
        if (onSaveCardChange) onSaveCardChange(!data.saveCard);
        saveData({...data, saveCard: !data.saveCard});
    };

    const handleNameChange = ({currentTarget}: SyntheticEvent<HTMLInputElement>) => {
        saveData({...data, cardholderName: currentTarget.value});
    };

    return (
        <form name="credit card form" onSubmit={handleOnSubmit}>
            {!cardholderName && (
                <Input
                    label="Name on Card"
                    name="name"
                    onChange={handleNameChange}
                    onInvalid={onInvalid}
                    value={data.cardholderName}
                />
            )}
            <div
                className={classNames(s.wrap, {
                    [s.facelift]: isFacelift,
                    [s.sideReview]: isSideReview,
                })}
            >
                <div className={s.card} />
                <div id="card-number" />
                <div id="exp-date" />
                <div id="cvv" />
                <PaymentCvvTooltip />
            </div>
            <p className={s.error} data-exp="cc-form-errors">
                {errors}
            </p>
            {withSave && (
                <div className={s.save}>
                    <Checkbox
                        ariaLabel="value switching"
                        isChecked={data.saveCard}
                        onChange={handleChangeSaveCard}
                        size="small"
                        value="Save details so checkout is even faster next time"
                    >
                        <span className={s.label}>
                            Save details so checkout is even faster next time
                        </span>
                    </Checkbox>
                </div>
            )}
            {children}
            {button ? (
                button(data.isProgress, handleOnSubmit)
            ) : (
                <CtaButton isLoading={data.isProgress} size="medium" type="submit">
                    Pay Now
                </CtaButton>
            )}
        </form>
    );
};

/**
 * PaymentsOS card form
 *
 * @param {PropsType} props - properties
 * @returns {JSX.Element} component
 */
export const PaymentOsCardForm: FC<PropsType> = props => {
    const isLoaded = useLoadScriptWithCheck(PAYMENT_OS_SCRIPT_SRC, 'POS', {remount: true});

    if (!isLoaded) return null;

    return <PaymentOsCardFormV3 {...props} />;
};
